<template>
  <div class="membership-card-view-mode-section">
    <switch-edit-mode-button-parts
      class="membership-card-view-mode-section__edit-button"
      type="edit"
      @click="emitEdit"
    />

    <div class="membership-card-view-mode-section__user-data">
      <figure class="membership-card-view-mode-section__image">
        <img
          :src="userThumbnail ? userThumbnail : require('@/assets/img/icon/icon_user_default.png')"
          alt=""
        />
      </figure>
      <p class="membership-card-view-mode-section__name" v-text="userName" />
    </div>

    <figure class="membership-card-view-mode-section__members-rank">
      <img
        v-if="isFreePlan"
        class="membership-card-view-mode-section__members-rank-badge membership-card-view-mode-section__members-rank-badge--free"
        :src="require('@/assets/img/MyPage/rank-free.png')"
        alt=""
      />
      <img
        v-else
        class="membership-card-view-mode-section__members-rank-badge"
        :src="require(`@/assets/img/MyPage/rank-${membersRankData.currentRank}.png`)"
        alt=""
      />
    </figure>

    <member-badge-list-parts
      v-if="userData.badgeList.length > 0"
      class="membership-card-view-mode-section__badge-list"
      :badgeList="userData.badgeList"
      :enabledListCarousel="enabledListCarousel"
    />

    <current-time-parts
      class="membership-card-view-mode-section__current-time"
      :hashedUserId="hashedUserId"
    />
  </div>
</template>

<script lang="ts">
import { computed, defineComponent, PropType, ref, onMounted } from '@vue/composition-api'
import FNV from 'fnv-plus'
import {
  UserDataType,
  MembersRankDataType,
} from '@/components/MypageTopPage/MembershipCardPane.vue'
import MemberBadgeListParts from '@/components/MypageTopPage/MembershipCardPane/parts/MemberBadgeListParts.vue'
import SwitchEditModeButtonParts from '@/components/MypageTopPage/MembershipCardPane/parts/SwitchEditModeButtonParts.vue'
import CurrentTimeParts from '@/components/MypageTopPage/MembershipCardPane/parts/CurrentTimeParts.vue'
import useMembershipCard from '@/components/MypageTopPage/hook/useMembershipCard'
import I18n from '@/locales/I18n'

/**
 * マイページ: 会員証 表示モードセクション
 */
export default defineComponent({
  name: 'MembershipCardViewModeSection',
  components: {
    SwitchEditModeButtonParts,
    MemberBadgeListParts,
    CurrentTimeParts,
  },
  props: {
    userData: {
      type: Object as PropType<UserDataType>,
      default: () => ({}),
      require: true,
    },
    membersRankData: {
      type: Object as PropType<MembersRankDataType>,
      default: () => ({}),
      require: true,
    },
    enabledListCarousel: {
      type: Boolean,
      default: false,
    },
  },
  setup(props) {
    const { getUserRoleColor, getUserType } = useMembershipCard()

    /** バーコード表示用のハッシュ化したユーザーID */
    const hashedUserId = ref('')

    const userName = computed(() => props.userData?.name || I18n.tc('common.displayNameNotSet'))
    const userThumbnail = computed(() => props.userData?.image)
    const userType = computed(() => getUserType(props.userData?.userRole))
    const roleColor = computed(() => getUserRoleColor(props.userData?.userRole))

    /**
     * 無料ユーザー判定取得
     */
    const isFreePlan = computed(() => props.userData.isFreePlan)

    /**
     * ユーザーIDを16桁のハッシュ値に変換する処理
     */
    const hashUserId = (userId: string) => FNV.fast1a64(userId).toString()

    onMounted(() => {
      hashedUserId.value = hashUserId(props.userData.id ?? '')
    })

    return {
      userName,
      userThumbnail,
      userType,
      roleColor,
      isFreePlan,
      hashedUserId,
    }
  },
  methods: {
    /**
     * 編集モードに切り替える
     */
    emitEdit() {
      this.$emit('edit')
    },
  },
})
</script>

<style lang="scss" scoped>
.membership-card-view-mode-section {
  display: flex;
  flex-flow: column nowrap;
  align-content: center;
  align-items: center;
  justify-content: center;
  justify-items: center;
  width: 100%;
  min-height: 218px;
  padding: 68px 5px 5px;
  margin: 0 auto;
  background: rgba(255, 255, 255, 0.2);
  border: 1px solid rgba(255, 255, 255, 0.2);
  backdrop-filter: blur(14.5px);
  border-radius: 10px;
  overflow: hidden;

  &__user-data {
    position: absolute;
    left: min(#{convertPXtoVW(10)}, 10px);
    top: min(#{convertPXtoVW(10)}, 10px);
    display: flex;
    flex-flow: row nowrap;
    align-items: center;
    justify-content: flex-start;
  }

  &__image {
    position: relative;
    z-index: 2;
    width: 30px;
    margin: 0 auto;

    img {
      width: 30px;
      height: 30px;
      border-radius: 50%;
      border: clamp(1.5px, #{convertPXtoVW(1.5)}, 2px) solid $white;
      overflow: hidden;
      object-fit: cover;
      object-position: center center;
    }
  }

  &__name {
    position: relative;
    z-index: 1;
    display: flex;
    padding: 2px 12px 2px 22px;
    margin-left: -15px;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    gap: 10px;
    border-radius: 0 40px 40px 0;
    background: rgba(0, 0, 0, 0.5);
    font-family: $fontSignup;
    font-weight: 700;
    font-size: 10px;
    line-height: 2;
    letter-spacing: 0.02em;
    font-feature-settings: 'palt' on;
    color: $white;
  }

  &__members-rank {
    display: block;
    width: 100%;

    &-badge {
      width: auto;
      height: min(#{convertPXtoVW(252)}, 252px);
      margin: 0 auto;

      &--free {
        height: min(#{convertPXtoVW(200)}, 200px);
      }
    }
  }

  &__badge-list {
    width: calc(100% + 20px);
    margin-top: 20px;
  }

  &__edit-button {
    right: min(#{convertPXtoVW(10)}, 10px);
    left: auto;
  }

  &__current-time {
    margin-top: 25px;
  }
}
</style>
